import React, { useState, useEffect } from 'react';
import OneSignal from 'react-onesignal';
import { Bell, X, Trophy, DollarSign, Users } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'bundle_notif_prompt_v1';
const SNOOZE_DAYS = 7;

/**
 * Branded in-app notification opt-in.
 * Shows 4 seconds after mount IF:
 *  - Browser supports Notification API
 *  - User hasn't permanently dismissed (denied or "never ask")
 *  - User hasn't snoozed within last 7 days
 *  - Permission is still 'default' (not granted/denied)
 */
export function NotificationPrompt() {
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Skip if browser doesn't support Notification API at all
    if (typeof Notification === 'undefined') return;
    // Already granted or denied — don't show
    if (Notification.permission !== 'default') return;

    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch {}
    if (stored?.dismissed_forever) return;
    if (stored?.snoozed_until && new Date(stored.snoozed_until) > new Date()) return;

    const timer = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  const handleAllow = async () => {
    setSubmitting(true);
    try {
      // Use OneSignal's prompt (handles Service Worker registration). Fallback to native.
      let granted = false;
      try {
        if (OneSignal?.Notifications?.requestPermission) {
          granted = await OneSignal.Notifications.requestPermission();
        } else if (OneSignal?.showNativePrompt) {
          await OneSignal.showNativePrompt();
          granted = Notification.permission === 'granted';
        } else {
          const result = await Notification.requestPermission();
          granted = result === 'granted';
        }
      } catch {
        const result = await Notification.requestPermission();
        granted = result === 'granted';
      }

      if (granted) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ granted_at: new Date().toISOString() }));
        } catch {}
      } else {
        // user denied via browser dialog → don't keep nagging
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissed_forever: true }));
        } catch {}
      }
    } finally {
      setSubmitting(false);
      setShow(false);
    }
  };

  const handleLater = () => {
    const until = new Date(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000).toISOString();
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ snoozed_until: until })); } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-20 md:bottom-6 md:right-6 md:left-auto md:max-w-sm z-[55] animate-in slide-in-from-bottom-5 duration-300"
      data-testid="notification-prompt"
    >
      <div className="relative rounded-2xl bg-white border-2 border-bundle-gold/50 shadow-2xl shadow-bundle-green/20 overflow-hidden">
        {/* Gold strip */}
        <div className="h-1.5 bg-gradient-to-r from-bundle-green via-bundle-gold to-bundle-green" />

        <button
          onClick={handleLater}
          className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-gray-100 z-10"
          data-testid="notif-prompt-close"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>

        <div className="p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-bundle-green to-bundle-green-dark flex items-center justify-center shadow-lg">
              <Bell className="w-5 h-5 text-bundle-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold text-bundle-green text-base leading-tight mb-0.5">
                Don't miss the action
              </h3>
              <p className="font-body text-xs text-gray-600">
                Turn on notifications to get pinged when:
              </p>
            </div>
          </div>

          <ul className="space-y-1.5 mb-4 ml-1">
            <li className="flex items-center gap-2 text-xs text-gray-700">
              <Users className="w-3.5 h-3.5 text-bundle-green flex-shrink-0" />
              Someone invites you to a game
            </li>
            <li className="flex items-center gap-2 text-xs text-gray-700">
              <Trophy className="w-3.5 h-3.5 text-bundle-gold-dark flex-shrink-0" />
              You move on the leaderboard
            </li>
            <li className="flex items-center gap-2 text-xs text-gray-700">
              <DollarSign className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
              You win a skin or bundle payout
            </li>
          </ul>

          <div className="flex gap-2">
            <Button
              onClick={handleLater}
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              data-testid="notif-prompt-later"
            >
              Not now
            </Button>
            <Button
              onClick={handleAllow}
              disabled={submitting}
              size="sm"
              className="flex-1 bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold text-xs"
              data-testid="notif-prompt-allow"
            >
              {submitting ? 'Asking...' : 'Notify Me'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
